import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DIVISIONS, OPP_STAGES, divisionLabel } from "@/lib/roles";
import { Plus, Archive, Undo2 } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLOR = {
  discovery: "#0EA5FF",
  proposal: "#F59E0B",
  negotiation: "#8B5CF6",
  won: "#5FBF3A",
  lost: "#EF4444",
};

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", division: "academy", stage: "discovery", value: 0, probability: 30 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = scope === "archived" ? { only_archived: true } : {};
    setItems((await api.get("/opportunities", { params })).data);
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(OPP_STAGES.map(s => [s.value, []]));
    for (const o of items) (map[o.stage] ||= []).push(o);
    return map;
  }, [items]);

  const totals = useMemo(() => {
    const t = { pipeline: 0, won: 0 };
    for (const o of items) {
      if (o.stage === "won") t.won += o.value || 0;
      else if (o.stage !== "lost") t.pipeline += (o.value || 0) * (o.probability || 0) / 100;
    }
    return t;
  }, [items]);

  const updateStage = async (id, stage) => {
    try {
      await api.patch(`/opportunities/${id}`, { stage });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this opportunity?")) return;
    await api.delete(`/opportunities/${id}`); toast.success("Archived"); load();
  };
  const restore = async (id) => {
    await api.post(`/opportunities/${id}/restore`); toast.success("Restored"); load();
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/opportunities", {
        ...form,
        value: Number(form.value) || 0,
        probability: Number(form.probability) || 0,
      });
      toast.success("Opportunity created");
      setOpen(false);
      setForm({ title: "", division: "academy", stage: "discovery", value: 0, probability: 30 });
      load();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout title="Opportunities">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div className="miro-card p-4">
          <div className="miro-overline">Weighted pipeline</div>
          <div className="font-display font-black text-2xl text-[var(--miro-navy)] mt-1">
            ₹{Math.round(totals.pipeline).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="miro-card p-4">
          <div className="miro-overline">Won revenue</div>
          <div className="font-display font-black text-2xl text-[var(--miro-green-600)] mt-1">
            ₹{Math.round(totals.won).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="miro-card p-4">
          <div className="miro-overline">Open</div>
          <div className="font-display font-black text-2xl text-[var(--miro-navy)] mt-1">
            {items.filter(o => o.stage !== "won" && o.stage !== "lost").length}
          </div>
        </div>
        <div className="miro-card p-4 flex items-center justify-between gap-2">
          <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
            {[["active","Active"],["archived","Archived"]].map(([v,l]) => (
              <button key={v} data-testid={`opps-scope-${v}`}
                      onClick={() => setScope(v)}
                      className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${scope === v ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}>
                {l}
              </button>
            ))}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-opp-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New opportunity</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New opportunity</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title *</Label><Input data-testid="new-opp-title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="mt-1"/></div>
                <div>
                  <Label>Division</Label>
                  <Select value={form.division} onValueChange={(v)=>setForm({...form,division:v})}>
                    <SelectTrigger data-testid="new-opp-division" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stage</Label>
                  <Select value={form.stage} onValueChange={(v)=>setForm({...form,stage:v})}>
                    <SelectTrigger data-testid="new-opp-stage" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{OPP_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Value (₹)</Label><Input data-testid="new-opp-value" type="number" value={form.value} onChange={(e)=>setForm({...form,value:e.target.value})} className="mt-1"/></div>
                <div><Label>Probability %</Label><Input data-testid="new-opp-probability" type="number" min={0} max={100} value={form.probability} onChange={(e)=>setForm({...form,probability:e.target.value})} className="mt-1"/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="new-opp-submit" disabled={!form.title.trim() || saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
                  {saving ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {OPP_STAGES.map(s => (
          <div key={s.value} data-testid={`opp-col-${s.value}`} className="kanban-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLOR[s.value] }}/>
                <div className="font-display font-bold text-sm text-[var(--miro-navy)]">{s.label}</div>
              </div>
              <span className="text-xs font-mono text-[var(--miro-text-muted)]">{byStage[s.value]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {(byStage[s.value] || []).map(o => (
                <div key={o.id} data-testid={`opp-card-${o.id}`} className="miro-card p-3">
                  <div className="font-semibold text-sm text-[var(--miro-navy)] leading-snug">{o.title}</div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--miro-text-muted)]">
                    <span>{divisionLabel(o.division)}</span>
                    <span className="font-mono text-[var(--miro-navy)] font-semibold">₹{(o.value || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] text-[var(--miro-text-muted)]">
                      <span>Probability</span><span>{o.probability}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[var(--miro-bg)] mt-1 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${o.probability}%`, background: STAGE_COLOR[s.value] }}/>
                    </div>
                  </div>
                  <Select value={o.stage} onValueChange={(v) => updateStage(o.id, v)}>
                    <SelectTrigger data-testid={`opp-stage-select-${o.id}`} className="mt-3 h-7 text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>{OPP_STAGES.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="mt-2 flex justify-end">
                    {scope === "archived" ? (
                      <button data-testid={`opp-restore-${o.id}`} onClick={() => restore(o.id)}
                              className="text-[11px] text-[var(--miro-text-muted)] hover:text-green-600 inline-flex items-center gap-1">
                        <Undo2 size={12}/> Restore
                      </button>
                    ) : (
                      <button data-testid={`opp-archive-${o.id}`} onClick={() => archive(o.id)}
                              className="text-[11px] text-[var(--miro-text-muted)] hover:text-red-600 inline-flex items-center gap-1">
                        <Archive size={12}/> Archive
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
